import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardNumberElement, CardExpiryElement, CardCvcElement, ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { toast } from "sonner";
import { Loader2, CreditCard, Wallet, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) stripePromise = api.get("/payments/config").then(({ data }) => loadStripe(data.publishable_key));
  return stripePromise;
};
const fmt = (n) => `AED ${Number(n) % 1 ? Number(n).toFixed(2) : Number(n)}`;
const GREEN = "bg-[#1A5C3A] hover:bg-[#154a2f]";

function PayButton({ amount, busy, disabled, onClick, type = "button", L, testid }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled || busy} data-testid={testid}
      className={`w-full h-14 rounded-2xl ${GREEN} text-white text-base font-extrabold flex items-center justify-center gap-2 transition-colors disabled:opacity-60`}>
      {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
      <span>{L("الدفع الآن", "Pay now")} · <span dir="ltr">{fmt(amount)}</span></span>
    </button>
  );
}

const CARD_STYLE = { style: { base: { fontSize: "16px", color: "#0f172a", fontFamily: "inherit", "::placeholder": { color: "#94a3b8" } }, invalid: { color: "#dc2626" } } };

function CardField({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold text-slate-600 mb-1">{label}</span>
      <div dir="ltr" className="h-12 px-3 flex items-center rounded-xl border-2 border-slate-200 bg-white focus-within:border-[#1A5C3A] [&>div]:w-full">{children}</div>
    </label>
  );
}

function CardForm({ co, onDone, L }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || busy) return;
    setBusy(true);
    const { error, paymentIntent } = await stripe.confirmCardPayment(co.client_secret, {
      payment_method: { card: elements.getElement(CardNumberElement), billing_details: { address: { country: "AE" } } },
    });
    if (error) { toast.error(error.message || L("فشل الدفع", "Payment failed")); setBusy(false); return; }
    onDone(paymentIntent?.id || co.payment_intent_id);
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-2xl bg-white border border-slate-200 p-4" data-testid="stripe-payment-form">
      <p className="text-sm font-extrabold text-slate-900 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#1A5C3A]" />{L("بيانات البطاقة", "Card details")}</p>
      <CardField label={L("رقم البطاقة", "Card number")}><CardNumberElement onReady={() => setReady(true)} options={{ ...CARD_STYLE, showIcon: true, disableLink: true }} /></CardField>
      <div className="grid grid-cols-2 gap-3">
        <CardField label={L("تاريخ الانتهاء", "Expiry")}><CardExpiryElement options={CARD_STYLE} /></CardField>
        <CardField label="CVC"><CardCvcElement options={CARD_STYLE} /></CardField>
      </div>
      {!ready && <div className="flex justify-center py-1"><Loader2 className="w-5 h-5 animate-spin text-[#1A5C3A]" /></div>}
      <PayButton type="submit" amount={co.card_amount} busy={busy} disabled={!stripe || !ready} L={L} testid="stripe-pay-submit" />
    </form>
  );
}

function WalletButton({ co, method, onDone, L }) {
  const stripe = useStripe();
  const elements = useElements();
  const confirm = async () => {
    const { error, paymentIntent } = await stripe.confirmPayment({ elements, redirect: "if_required", confirmParams: { return_url: `${window.location.origin}/payment/success` } });
    if (error) return toast.error(error.message || L("فشل الدفع", "Payment failed"));
    onDone(paymentIntent?.id || co.payment_intent_id);
  };
  const only = { applePay: method === "apple_pay" ? "always" : "never", googlePay: method === "google_pay" ? "always" : "never", link: "never", paypal: "never", amazonPay: "never", klarna: "never" };
  return <div data-testid={`express-${method}`}><ExpressCheckoutElement onConfirm={confirm} options={{ paymentMethods: only, buttonHeight: 52, phoneNumberRequired: false, emailRequired: false, billingAddressRequired: false, shippingAddressRequired: false }} /></div>;
}

function PlanPicker({ plans, days, setDays, L }) {
  return (
    <div className="space-y-2.5" data-testid="promo-plans">
      {plans.map((p) => {
        const on = days === p.days;
        return (
          <button key={p.days} type="button" onClick={() => setDays(p.days)} data-testid={`promo-plan-${p.days}`}
            className={`w-full flex items-center gap-3 h-14 px-4 rounded-2xl border-2 bg-white transition-colors ${on ? "border-[#C9A227] bg-[#FBF6E6]" : "border-slate-200 hover:border-slate-300"}`}>
            <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${on ? "border-[#1A5C3A]" : "border-slate-300"}`}>{on && <span className="w-2.5 h-2.5 rounded-full bg-[#1A5C3A]" />}</span>
            <span className="flex-1 text-start font-bold text-slate-800">{p.days} {L("يوم", "days")}</span>
            <span dir="ltr" className="font-extrabold text-[#1A5C3A]">{fmt(p.price)}</span>
          </button>
        );
      })}
    </div>
  );
}

function MethodCard({ id, icon: Icon, title, sub, active, disabled, last, onPick, L }) {
  return (
    <button type="button" disabled={disabled} onClick={() => onPick(id)} data-testid={`pay-method-${id}`}
      className={`w-full flex items-center gap-3 p-3.5 rounded-2xl border-2 bg-white text-start transition-colors disabled:opacity-45 ${active ? "border-[#1A5C3A] bg-[#EEF5F0]" : "border-slate-200 hover:border-slate-300"}`}>
      <span className={`w-11 h-11 shrink-0 rounded-xl flex items-center justify-center ${active ? "bg-[#1A5C3A] text-white" : "bg-[#F4F2EC] text-[#1A5C3A]"}`}><Icon className="w-5 h-5" /></span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2 font-bold text-sm text-slate-900">{title}
          {last && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#C9A227]/15 text-[#8A6D12]" data-testid={`pay-last-used-${id}`}>{L("آخر استخدام", "Last used")}</span>}</span>
        {sub && <span className="block text-xs text-slate-500 mt-0.5">{sub}</span>}
      </span>
      <span className={`w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center ${active ? "border-[#1A5C3A]" : "border-slate-300"}`}>{active && <span className="w-2.5 h-2.5 rounded-full bg-[#1A5C3A]" />}</span>
    </button>
  );
}

function MethodPicker({ q, avail, method, setMethod, L }) {
  const short = Math.max(0, Math.round((q.amount - q.wallet_balance) * 100) / 100);
  const fromWallet = Math.min(q.wallet_balance, q.amount - q.min_card);
  const quick = avail.applePay ? "apple_pay" : avail.googlePay ? "google_pay" : null;
  const props = (id) => ({ id, active: method === id, last: q.last_method === id, onPick: setMethod, L });
  return (
    <div className="space-y-2.5" data-testid="pay-method-picker">
      <MethodCard {...props("card")} icon={CreditCard} title={L("بطاقة بنكية", "Bank card")} sub={L("فيزا / ماستركارد عبر Stripe", "Visa / Mastercard via Stripe")} />
      {quick ? <MethodCard {...props(quick)} icon={Smartphone} title={quick === "apple_pay" ? "Apple Pay" : "Google Pay"} sub={L("الدفع السريع من جهازك", "Quick pay from your device")} />
        : <MethodCard {...props("apple_pay")} disabled icon={Smartphone} title="Apple Pay" sub={L("غير متاح على هذا الجهاز", "Not available on this device")} />}
      <MethodCard {...props("wallet")} icon={Wallet} title={L("الدفع من رصيد المحفظة", "Pay from wallet balance")} disabled={q.wallet_balance <= 0}
        sub={<>{L("رصيد المحفظة", "Wallet balance")}: <span dir="ltr">{fmt(q.wallet_balance)}</span></>} />
      {method === "wallet" && short > 0 && q.wallet_balance > 0 && (
        <p className="text-xs font-semibold text-amber-700 bg-amber-50 rounded-xl p-2.5" data-testid="pay-wallet-split">
          {L("سيتم الدفع", "You'll pay")} <span dir="ltr">{fmt(fromWallet)}</span> {L("من المحفظة +", "from wallet +")} <span dir="ltr">{fmt(q.amount - fromWallet)}</span> {L("بالبطاقة", "by card")}
        </p>
      )}
    </div>
  );
}

// Unified payment dialog: req = { listing_id, promo_days? } or { listing_id, promo: true, renew? } to pick the duration here.
export function PaymentDialog({ req, onClose }) {
  const { lang, dir } = useLang();
  const L = (ar, en) => (lang === "en" ? en : ar);
  const navigate = useNavigate();
  const [stripe, setStripe] = useState(null);
  const [plans, setPlans] = useState(null);
  const [days, setDays] = useState(req.promo_days || null);
  const [q, setQ] = useState(null);
  const [avail, setAvail] = useState(null);
  const [method, setMethod] = useState("card");
  const [co, setCo] = useState(null);
  const [busy, setBusy] = useState(false);
  const coRef = useRef(null);
  coRef.current = co;
  const isPromo = !!(req.promo || req.promo_days);
  const params = { listing_id: req.listing_id, ...(isPromo && days ? { promo_days: days } : {}) };
  const fail = (err) => { toast.error(formatApiErrorDetail(err.response?.data?.detail) || L("تعذّر تحميل الدفع", "Couldn't load payment")); onClose(); };

  useEffect(() => {
    getStripe().then(async (s) => {
      setStripe(s);
      let a = {};
      try { a = (await s.paymentRequest({ country: "AE", currency: "aed", total: { label: "Bazaroo", amount: 100 } }).canMakePayment()) || {}; } catch { a = {}; }
      setAvail(a);
    }).catch(fail);
    if (req.promo) api.get("/promotion-packages").then(({ data }) => { setPlans(data); setDays((d) => d || data[0]?.days || null); }).catch(fail);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!avail || (req.promo && !days)) return;
    api.get("/payments/quote", { params }).then(({ data }) => {
      setQ((prev) => {
        if (!prev) {
          const last = data.last_method;
          setMethod((last === "apple_pay" && avail.applePay) || (last === "google_pay" && avail.googlePay) || (last === "wallet" && data.wallet_balance > 0) ? last : "card");
        }
        return data;
      });
    }).catch(fail);
  }, [avail, days]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = (id) => { coRef.current = null; navigate(`/payment/success?payment_intent=${id}`); };
  const release = async () => {
    const c = coRef.current;
    if (!c || c.paid || !(c.wallet_amount > 0)) return true;
    try { await api.post(`/payments/${c.payment_intent_id}/cancel`); coRef.current = null; return true; }
    catch { toast.error(L("تعذّر إلغاء الحجز، حاول مرة أخرى", "Couldn't release the reserved amount, try again")); return false; }
  };
  const close = async () => { if (await release()) onClose(); };
  const proceed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { ...params, method });
      if (data.paid) return done(data.payment_intent_id);
      setCo(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const back = async () => { if (await release()) { setCo(null); api.get("/payments/quote", { params }).then(({ data }) => setQ(data)).catch(() => {}); } };

  const title = isPromo ? (req.renew ? L("جدّد تمييز إعلانك ☆", "Renew your featured ad ☆") : L("اجعل إعلانك مميزاً ☆", "Make your ad featured ☆")) : L("رسوم نشر الإعلان", "Ad posting fee");
  const desc = isPromo
    ? L("يظهر الإعلان المميز في أعلى الصفحة الرئيسية ونتائج البحث. تتم معالجة الدفع بأمان عبر Stripe.", "Featured ads appear at the top of the home page and search results. Payment is processed securely via Stripe.")
    : L("ادفع رسوم النشر بعد موافقة الإدارة على إعلانك ليتم نشره. تتم معالجة الدفع بأمان عبر Stripe.", "Pay the posting fee after admin approval to publish your ad. Payment is processed securely via Stripe.");
  const express = co && (method === "apple_pay" || method === "google_pay");
  const loading = !q || !stripe || (req.promo && !plans);

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent dir={dir} className="max-w-md max-h-[92vh] overflow-y-auto rounded-3xl bg-[#FAF8F3] p-5 sm:p-6" data-testid="stripe-payment-dialog">
        <div className="space-y-1.5 text-start">
          <DialogTitle className="font-headline text-xl font-extrabold text-slate-900" data-testid="pay-title">{title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500 leading-relaxed" data-testid="pay-description">{desc}</DialogDescription>
        </div>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-[#1A5C3A]" /></div> : (
          <div className="space-y-4">
            {req.promo && !co && <PlanPicker plans={plans} days={days} setDays={setDays} L={L} />}
            {!req.promo && (
              <div className="flex items-center justify-between rounded-2xl bg-white border-2 border-[#C9A227] px-4 h-14" data-testid="pay-amount">
                <span className="text-sm font-bold text-slate-700">{L("المبلغ المطلوب", "Amount due")}</span><b dir="ltr" className="text-lg text-[#1A5C3A]">{fmt(q.amount)}</b>
              </div>
            )}
            {!co ? <>
              <p className="text-sm font-extrabold text-slate-900 pt-1">{L("اختر طريقة الدفع", "Choose payment method")}</p>
              <MethodPicker q={q} avail={avail} method={method} setMethod={setMethod} L={L} />
              <p className="text-[11px] text-slate-500 text-center" data-testid="pay-footer-note">{L("دفع آمن عبر Stripe · شحن المحفظة يتم بالتحويل البنكي فقط", "Secure payment via Stripe · Wallet top-up is by bank transfer only")}</p>
              <PayButton amount={q.amount} busy={busy} onClick={proceed} L={L} testid="pay-continue" />
            </> : <>
              {co.wallet_amount > 0 && <p className="text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-xl p-2.5" data-testid="pay-wallet-held">{L("تم حجز", "Reserved")} <span dir="ltr">{fmt(co.wallet_amount)}</span> {L("من المحفظة، أكمل", "from wallet, pay")} <span dir="ltr">{fmt(co.card_amount)}</span> {L("بالبطاقة", "by card")}</p>}
              {express ? (
                <Elements key="express" stripe={stripe} options={{ clientSecret: co.client_secret, locale: lang === "en" ? "en" : "ar", appearance: { theme: "stripe", variables: { colorPrimary: "#1A5C3A", borderRadius: "12px" } } }}>
                  <WalletButton co={co} method={method} onDone={done} L={L} />
                </Elements>
              ) : (
                <Elements key="card" stripe={stripe} options={{ locale: lang === "en" ? "en" : "ar" }}>
                  <CardForm co={co} onDone={done} L={L} />
                </Elements>
              )}
              <button onClick={back} className="w-full text-sm text-slate-500 hover:underline" data-testid="pay-change-method">{L("تغيير طريقة الدفع", "Change payment method")}</button>
            </>}
            <button onClick={close} className="w-full text-sm font-bold text-slate-500 hover:text-slate-800 py-1" data-testid="pay-cancel">{L("إلغاء", "Cancel")}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
